import React from 'react';
import { motion } from 'framer-motion';

const publicUrl = process.env.PUBLIC_URL || '';

const services = [
  {
    image: `${publicUrl}/images/software-development.png`,
    title: 'Software Development',
    color: '#ff6b00',
    description: 'Custom web, desktop, and cross-platform mobile applications engineered to scale.',
    features: ['Web Applications', 'Desktop Applications', 'Mobile (iOS & Android)', 'API & Integrations'],
  },
  {
    image: `${publicUrl}/images/web-email-hosting.jpg`,
    title: 'Web & Email Services',
    color: '#ff6b00',
    description: 'Complete online presence solutions — from domains to professional email hosting.',
    features: ['Website Design & Development', 'Domain Registration', 'Professional Email Hosting', 'Payment Systems Integration'],
  },
  {
    image: `${publicUrl}/images/microsoft-cloud.jpg`,
    title: 'Microsoft Cloud',
    color: '#ff6b00',
    description: 'As a trusted Microsoft Partner, we deliver Azure, M365, and Dynamics 365 solutions.',
    features: ['Azure Cloud Services', 'Microsoft 365', 'Dynamics 365', 'Power Platform & Analytics'],
  },
  {
    image: `${publicUrl}/images/it-consulting.jpg`,
    title: 'IT Consulting',
    color: '#ff6b00',
    description: 'Proactive cybersecurity and strategic IT support to keep your business protected.',
    features: ['Outsourced IT Support', 'IT & Security Audits', 'Security Solutions', 'Strategic IT Consulting'],
  },
  {
    image: `${publicUrl}/images/business.jpg`,
    title: 'Business Systems',
    color: '#ff6b00',
    description: 'Enterprise-grade CRM, ERP, and HRMS systems that streamline your operations.',
    features: ['CRM (Dynamics 365)', 'ERP Solutions', 'Loan Management Systems', 'HRMS & Help Desk'],
  },
];

function ServiceCard({ service, index }: { service: typeof services[0], index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      style={{
        padding: '1.75rem',
        background: `linear-gradient(145deg, ${service.color}12, rgba(255,255,255,0.95))`,
        border: `1px solid ${service.color}40`,
        borderRadius: 20,
        transition: 'all 0.35s ease',
        transform: 'translateY(-4px)',
        boxShadow: `0 20px 60px ${service.color}15`,
      }}
    >
      <div style={{
        width: '100%', height: 180, borderRadius: 12,
        background: `rgba(255,255,255,255)`,
        border: `1px solid ${service.color}40`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: '1.25rem',
        overflow: 'hidden',
      }}>
        <img 
          src={service.image} 
          alt={service.title}
          style={{
            width: '100%',
            height: '80%',
            objectFit: 'fill',
          }}
        />
      </div>
      <h3 style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.6rem', color: 'var(--text)' }}>{service.title}</h3>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-soft)', lineHeight: 1.6, marginBottom: '1.25rem' }}>
        {service.description}
      </p>
      <motion.ul
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}
      >
        {service.features.map(f => (            <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: 'var(--text)' }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--orange)', flexShrink: 0 }} />
            {f}
          </li>
        ))}
      </motion.ul>
    </motion.div>
  );
}

export default function Services() {
  return (
    <section id="services" style={{ padding: '8rem 2rem', position: 'relative' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '4rem' }}
        >
          <span style={{
            display: 'inline-block', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: 'var(--text)', marginBottom: '1rem',
            padding: '0.3rem 0.9rem', borderRadius: 100,
            background: 'var(--electric-dim)', border: '1px solid var(--gray-light)',
          }}>What We Do</span>
          <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '1rem', letterSpacing: '-0.02em', color: 'var(--text)' }}>
            Five Pillars of <span style={{ color: 'var(--orange)' }}>Digital Excellence</span>
          </h2>
          <p style={{ fontSize: '1rem', color: 'var(--text-soft)', maxWidth: 540, margin: '0 auto' }}>
            From custom software to cloud infrastructure — we cover every layer of your technology needs.
          </p>
        </motion.div>

        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '2rem', maxWidth: 1200, margin: '0 auto' }}>
          {services.map((s, i) => (
            <div key={i} style={{ width: 280, flexShrink: 0 }}>
              <ServiceCard service={s} index={i} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
